/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useRef, useState } from 'react';

import { z } from 'zod';

import type { ConversationContext } from '@/contexts/ChatSidebarContextDef';
import type { SelectedProductInfo } from '@/contexts/ChatSidebarContextDef';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import type { Json } from '@/integrations/supabase/types';
import { createAbortError, isAbortError } from '@/lib/abortError';
import { detectErrorType, getErrorMessages } from '@/lib/rateLimitErrorHandler';
import type { PageContext } from '@/types/pageContext';

import type { FileContext } from '@/components/chat/parseAttachedFiles';
import { FileContextSchema } from '@/components/chat/parseAttachedFiles';

import { PostHogEventName, usePostHogTracking } from './usePostHogTracking';

export type ChatMessageRole = 'user' | 'assistant';

export interface ChatMessage {
  id: string;
  content: string;
  role: ChatMessageRole;
  created_at: Date;
  metadata?: Record<string, unknown>;
  status?: 'pending' | 'sent' | 'failed' | 'interrupted';
  errorType?: 'rate_limit' | 'connection' | 'unknown';
}

export interface ChatConversation {
  id: string;
  title: string;
  created_at: Date;
  updated_at: Date;
}

const SendMessageSchema = z.object({
  message: z.string().min(1, 'Message cannot be empty'),
  conversationId: z.string().optional(),
  userMessageId: z.string().optional(),
  toolId: z.string().optional(),
  pageContext: z
    .object({
      pageType: z.string(),
    })
    .passthrough()
    .optional(),
  selectedProducts: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      })
    )
    .optional(),
  fileContexts: z.array(FileContextSchema).optional(),
});

interface SendMessageOptions {
  toolId?: string;
  source?: 'tool' | 'module' | 'dashboard';
  metadata?: Record<string, unknown>;
  conversationIdOverride?: string;
  pageContext?: PageContext | null;
  selectedProducts?: Array<{ id: string; name: string }>;
  fileContexts?: FileContext[];
}

export const useChat = () => {
  const { user } = useAuth();
  const supabase = useSupabaseClient();
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<
    string | null
  >(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingAssistantForMessageId, setPendingAssistantForMessageId] =
    useState<string | null>(null);
  const [isResponding, setIsResponding] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { trackEvent } = usePostHogTracking();
  const activeLoadCountRef = useRef(0);

  const beginLoad = useCallback(() => {
    activeLoadCountRef.current += 1;
    setIsLoading(true);
  }, []);

  const endLoad = useCallback(() => {
    activeLoadCountRef.current -= 1;
    if (activeLoadCountRef.current < 0) {
      activeLoadCountRef.current = 0;
    }

    setIsLoading(activeLoadCountRef.current > 0);
  }, []);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setCurrentConversationId(null);
    }
  }, [user]);

  const loadConversations = useCallback(async () => {
    if (!user) {
      return;
    }

    beginLoad();
    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) {
        throw error;
      }

      setConversations(
        data.map((conversation) => ({
          id: conversation.id,
          title: conversation.title,
          created_at: new Date(conversation.created_at),
          updated_at: new Date(conversation.updated_at),
        }))
      );
    } catch {
      void 0;
    } finally {
      endLoad();
    }
  }, [user, supabase, beginLoad, endLoad]);

  const loadMessages = useCallback(
    async (conversationId: string) => {
      beginLoad();
      try {
        const { data, error } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('conversation_id', conversationId)
          .order('created_at', { ascending: true });

        if (error) {
          throw error;
        }

        setMessages(
          data.map((message) => {
            const metadata =
              (message.metadata as Record<string, unknown>) || {};
            return {
              id: message.id,
              content: message.content,
              role: message.role as ChatMessageRole,
              created_at: new Date(message.created_at),
              metadata,
              status: metadata.stopped ? 'interrupted' : undefined,
            };
          })
        );
      } catch {
        setMessages([]);
      } finally {
        endLoad();
      }
    },
    [supabase, beginLoad, endLoad]
  );

  const createConversation = useCallback(
    async (title = 'New Conversation', context?: ConversationContext) => {
      if (!user) {
        return null;
      }

      try {
        const { data, error } = await supabase
          .from('chat_conversations')
          .insert({
            user_id: user.id,
            title,
            ...(context?.toolId && { tool_id: context.toolId }),
            ...(context?.toolVersionId && {
              tool_version_id: context.toolVersionId,
            }),
            ...(context?.moduleId && { module_id: context.moduleId }),
          })
          .select()
          .single();

        if (error) {
          throw error;
        }

        const newConversation: ChatConversation = {
          id: data.id,
          title: data.title,
          created_at: new Date(data.created_at),
          updated_at: new Date(data.updated_at),
        };

        setConversations((prev) => [newConversation, ...prev]);
        setCurrentConversationId(data.id);
        setMessages([]);

        return data.id;
      } catch {
        return null;
      }
    },
    [user, supabase, currentConversationId]
  );

  const switchConversation = useCallback(
    async (conversationId: string) => {
      setCurrentConversationId(conversationId);
      await loadMessages(conversationId);
    },
    [loadMessages]
  );

  const insertMessage = useCallback(
    async (params: {
      conversationId: string;
      content: string;
      role: ChatMessageRole;
      metadata?: Record<string, unknown>;
    }): Promise<ChatMessage | null> => {
      try {
        const { data, error } = await supabase
          .from('chat_messages')
          .insert({
            conversation_id: params.conversationId,
            content: params.content,
            role: params.role,
            metadata: (params.metadata || {}) as Json,
          })
          .select()
          .single();

        if (error) {
          throw error;
        }

        const { error: updateConversationError } = await supabase
          .from('chat_conversations')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', params.conversationId);

        if (updateConversationError) {
          console.error(
            'Failed to update conversation timestamp:',
            updateConversationError
          );
        }

        return {
          id: data.id,
          content: data.content,
          role: data.role as ChatMessageRole,
          created_at: new Date(data.created_at),
          metadata: (data.metadata as Record<string, unknown>) || {},
        };
      } catch (error) {
        console.error('Failed to persist message:', error);
        return null;
      }
    },
    [supabase]
  );

  const addMessage = useCallback(
    async (
      content: string,
      role: ChatMessageRole,
      metadata?: Record<string, unknown>,
      conversationIdOverride?: string
    ) => {
      const targetConversationId =
        conversationIdOverride || currentConversationId;
      if (!targetConversationId) {
        return null;
      }

      const message = await insertMessage({
        conversationId: targetConversationId,
        content,
        role,
        metadata,
      });

      if (message) {
        setMessages((prev) => [...prev, message]);
      }

      return message;
    },
    [currentConversationId, insertMessage]
  );

  const updateConversationTitle = useCallback(
    async (conversationId: string, title: string) => {
      try {
        const { error } = await supabase
          .from('chat_conversations')
          .update({ title })
          .eq('id', conversationId);

        if (error) {
          throw error;
        }

        setConversations((prev) =>
          prev.map((conversation) =>
            conversation.id === conversationId
              ? { ...conversation, title }
              : conversation
          )
        );
      } catch {
        // Silent error handling
      }
    },
    [supabase]
  );

  const beginResponding = useCallback((messageId: string): AbortController => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setPendingAssistantForMessageId(messageId);
    setIsResponding(true);
    return controller;
  }, []);

  const endResponding = useCallback(() => {
    abortControllerRef.current = null;
    setPendingAssistantForMessageId(null);
    setIsResponding(false);
  }, []);

  const delay = (milliseconds: number, signal: AbortSignal): Promise<void> =>
    new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(createAbortError());
        return;
      }
      const timeoutId = setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      }, milliseconds);
      const onAbort = () => {
        clearTimeout(timeoutId);
        reject(createAbortError());
      };
      signal.addEventListener('abort', onAbort, { once: true });
    });

  const exponentialBackoffWithJitter = (attempt: number) => {
    const baseDelay = 400;
    const maxDelay = 1600;
    const exponentialDelay = Math.min(
      baseDelay * Math.pow(2, attempt),
      maxDelay
    );
    const jitter = Math.random() * 0.3 * exponentialDelay;
    return exponentialDelay + jitter;
  };

  const readSSEStream = async (
    response: Response,
    onDelta?: (delta: string, fullResponse: string) => void
  ): Promise<{ response: string; title: string | null }> => {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body for streaming');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let fullResponse = '';
    let generatedTitle: string | null = null;

    const processSSELine = (line: string) => {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data: ')) {
        return;
      }

      const jsonStr = trimmed.slice(6);

      if (!jsonStr || jsonStr === '[DONE]') {
        return;
      }

      try {
        const data = JSON.parse(jsonStr);

        if (data.type === 'title' && data.title) {
          generatedTitle = data.title;
        } else if (data.type === 'delta' && data.delta) {
          fullResponse += data.delta;
          onDelta?.(data.delta, fullResponse);
        } else if (data.type === 'error') {
          throw new Error(data.error || 'Stream error');
        }
      } catch (parseError) {
        if (!(parseError instanceof SyntaxError)) {
          throw parseError;
        }
        console.debug('Failed to parse SSE data:', jsonStr.substring(0, 50));
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          processSSELine(line);
        }
      }

      if (buffer.trim()) {
        processSSELine(buffer);
      }
    } finally {
      reader.releaseLock();
    }

    if (!fullResponse) {
      throw new Error('No response received from AI');
    }

    return { response: fullResponse, title: generatedTitle };
  };

  const getUIErrorType = (
    errorType: ReturnType<typeof detectErrorType>
  ): 'rate_limit' | 'connection' | 'unknown' => {
    if (errorType.isRateLimitError) {
      return 'rate_limit';
    }
    if (errorType.isAuthError || errorType.isValidationError) {
      return 'unknown';
    }
    return 'connection';
  };

  const retryWithExponentialBackoff = async <T,>(
    operation: () => Promise<T>,
    userMessageId: string,
    signal: AbortSignal,
    maxRetries: number = 3
  ): Promise<{ success: boolean; data?: T }> => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (signal.aborted) {
        throw createAbortError();
      }

      try {
        const result = await operation();
        return { success: true, data: result };
      } catch (error: unknown) {
        if (isAbortError(error)) {
          throw error;
        }

        const isLastAttempt = attempt === maxRetries - 1;
        const errorType = detectErrorType(error);
        const isRetriableError =
          !errorType.isAuthError &&
          !errorType.isValidationError &&
          !errorType.isRateLimitError;

        if (!isRetriableError || isLastAttempt) {
          const uiErrorType = getUIErrorType(errorType);

          let messages: { title: string; description: string };
          if (errorType.isAuthError) {
            messages = {
              title: 'Authentication issue',
              description:
                'There was an authentication problem. Please refresh the page and try again.',
            };
          } else if (errorType.isValidationError) {
            messages = {
              title: 'Invalid request',
              description:
                'There was a problem with your request. Please try rephrasing your message.',
            };
          } else {
            messages = getErrorMessages(error, {
              rateLimitContext: 'messages',
              genericAction: 'connect to Stella',
            });
          }

          setMessages((prev) =>
            prev.map((message) =>
              message.id === userMessageId
                ? {
                    ...message,
                    status: 'failed' as const,
                    errorType: uiErrorType,
                  }
                : message
            )
          );

          const showRetry =
            !errorType.isAuthError && !errorType.isValidationError;

          toast({
            title: messages.title,
            description: messages.description,
            variant: 'destructive',
            action: showRetry ? (
              <button
                type="button"
                onClick={() => retryFailedMessage(userMessageId)}
                className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-input"
              >
                Retry
              </button>
            ) : undefined,
          });

          return { success: false };
        }

        if (attempt < maxRetries - 1) {
          const delayMs = exponentialBackoffWithJitter(attempt);
          await delay(delayMs, signal);
        }
      }
    }
    return { success: false };
  };

  const persistInterruptedAssistantMessage = useCallback(
    async (
      conversationId: string,
      content: string,
      metadata: Record<string, unknown>
    ): Promise<ChatMessage | null> => {
      const message = await insertMessage({
        conversationId,
        content,
        role: 'assistant',
        metadata,
      });

      if (!message) {
        toast({
          title: "Couldn't save stopped response",
          description:
            'The interrupted response could not be saved and may be lost on refresh.',
          variant: 'destructive',
        });
        return null;
      }

      return { ...message, status: 'interrupted' as const };
    },
    [insertMessage]
  );

  const retryFailedMessage = useCallback(
    async (messageId: string) => {
      const failedMessage = messages.find(
        (message) => message.id === messageId
      );
      if (!failedMessage) {
        return;
      }

      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? { ...message, status: 'pending' as const }
            : message
        )
      );

      const controller = beginResponding(messageId);

      const payload = {
        message: failedMessage.content,
        conversationId:
          failedMessage.metadata?.conversationId || currentConversationId,
        toolId: failedMessage.metadata?.toolId,
        pageContext: failedMessage.metadata?.pageContext ?? undefined,
        selectedProducts: failedMessage.metadata?.selectedProducts as
          | Array<{ id: string; name: string }>
          | undefined,
      };

      let latestPartialContent = '';

      try {
        const validationResult = SendMessageSchema.safeParse(payload);
        if (!validationResult.success) {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === messageId
                ? { ...message, status: 'failed' as const }
                : message
            )
          );
          return;
        }

        const operation = async () => {
          if (controller.signal.aborted) {
            throw createAbortError();
          }

          const { data: streamResponse, error: invokeError } =
            await supabase.functions.invoke<Response>(
              'stella-api/chat/stream',
              {
                body: validationResult.data,
                signal: controller.signal,
              }
            );

          if (controller.signal.aborted) {
            throw createAbortError();
          }

          if (invokeError) {
            throw invokeError;
          }

          if (!streamResponse) {
            throw new Error('Chat error: empty response');
          }

          return await readSSEStream(streamResponse, (_delta, fullResponse) => {
            latestPartialContent = fullResponse;
          });
        };

        const retryConversationId =
          (failedMessage.metadata?.conversationId as string | undefined) ||
          currentConversationId;

        const result = await retryWithExponentialBackoff(
          operation,
          messageId,
          controller.signal
        ).catch(async (error) => {
          if (!isAbortError(error)) {
            return { success: false as const };
          }

          const interruptedMetadata: Record<string, unknown> = {
            type: failedMessage.metadata?.moduleId
              ? 'module_guidance'
              : 'tool_guidance',
            ...failedMessage.metadata,
            streaming: false,
            stopped: true,
          };

          const interruptedMessage = retryConversationId
            ? await persistInterruptedAssistantMessage(
                retryConversationId,
                latestPartialContent,
                interruptedMetadata
              )
            : null;

          setMessages((prev) => {
            const updated = prev.map((message) =>
              message.id === messageId
                ? { ...message, status: 'sent' as const }
                : message
            );
            return interruptedMessage
              ? [...updated, interruptedMessage]
              : updated;
          });

          return { success: false as const };
        });

        if (result.success) {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === messageId
                ? { ...message, status: 'sent' as const }
                : message
            )
          );

          await addMessage(result.data.response, 'assistant', {
            type: failedMessage.metadata?.moduleId
              ? 'module_guidance'
              : 'tool_guidance',
            ...failedMessage.metadata,
          });

          if (result.data?.title && retryConversationId) {
            await updateConversationTitle(
              retryConversationId,
              result.data.title
            );
          }
        }
      } finally {
        endResponding();
      }
    },
    [
      messages,
      currentConversationId,
      supabase,
      addMessage,
      updateConversationTitle,
      beginResponding,
      endResponding,
      persistInterruptedAssistantMessage,
    ]
  );

  const sendMessage = useCallback(
    async (
      content: string,
      options?: SendMessageOptions & {
        conversationId?: string;
        selectedProductsOverride?: Record<string, SelectedProductInfo>;
        toolContext?: {
          toolName: string;
          toolRoute: string;
          data?: unknown;
        } | null;
        userMetadata?: Record<string, unknown>;
        assistantMetadata?: Record<string, unknown>;
      }
      // eslint-disable-next-line complexity
    ) => {
      const conversationIdToUse =
        options?.conversationId || currentConversationId;

      if (!conversationIdToUse || !user) {
        return false;
      }

      let placeholderAssistantId: string | null = null;
      let userMessageId: string | null = null;
      let latestPartialContent = '';

      try {
        const userMeta: Record<string, unknown> = {
          ...options?.userMetadata,
          conversationId: conversationIdToUse,
          toolId: options?.toolId,
          source: options?.source,
        };

        if (options?.toolContext) {
          userMeta.toolContext = options.toolContext;
        }

        const selectedProducts =
          options?.selectedProductsOverride !== undefined
            ? Object.values(options.selectedProductsOverride).map(
                (product) => ({
                  id: product.id,
                  name: product.name,
                })
              )
            : (options?.selectedProducts ?? []);

        if (selectedProducts.length > 0) {
          userMeta.selectedProducts = selectedProducts;
        }

        if (options?.pageContext) {
          userMeta.pageContext = options.pageContext;
        }

        if (options?.fileContexts && options.fileContexts.length > 0) {
          userMeta.fileAttachments = options.fileContexts.map((file) => ({
            filename: file.filename,
            fileSize: file.fileSize,
          }));
        }

        const userMessage = await addMessage(
          content,
          'user',
          userMeta,
          conversationIdToUse
        );
        if (!userMessage) {
          throw new Error('Failed to add user message');
        }
        userMessageId = userMessage.id;

        setMessages((prev) =>
          prev.map((message) =>
            message.id === userMessage.id
              ? { ...message, status: 'pending' as const }
              : message
          )
        );
        const controller = beginResponding(userMessage.id);

        const payload = {
          message: content,
          conversationId: conversationIdToUse,
          userMessageId: userMessage.id,
          toolId: options?.toolId,
          pageContext: options?.pageContext ?? undefined,
          selectedProducts:
            selectedProducts.length > 0 ? selectedProducts : undefined,
          fileContexts:
            options?.fileContexts && options.fileContexts.length > 0
              ? options.fileContexts
              : undefined,
        };

        const validationResult = SendMessageSchema.safeParse(payload);
        if (!validationResult.success) {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === userMessage.id
                ? { ...message, status: 'failed' as const }
                : message
            )
          );
          return false;
        }

        const userMessageCount = messages.filter(
          (message) => message.role === 'user'
        ).length;

        if (userMessageCount === 0) {
          void trackEvent(PostHogEventName.UserFirstPromptSubmitted, {
            conversationId: conversationIdToUse,
            toolId: options?.toolId,
          });
        }

        placeholderAssistantId = `streaming-${Date.now()}-${userMessage.id}`;
        setMessages((prev) => [
          ...prev,
          {
            id: placeholderAssistantId,
            content: '',
            role: 'assistant' as const,
            created_at: new Date(),
            metadata: { ...options?.assistantMetadata, streaming: true },
            status: 'pending' as const,
          },
        ]);

        const operation = async () => {
          if (controller.signal.aborted) {
            throw createAbortError();
          }

          const { data: streamResponse, error: invokeError } =
            await supabase.functions.invoke<Response>(
              'stella-api/chat/stream',
              {
                body: validationResult.data,
                signal: controller.signal,
              }
            );

          if (controller.signal.aborted) {
            throw createAbortError();
          }

          if (invokeError) {
            throw invokeError;
          }

          if (!streamResponse) {
            throw new Error('Chat error: empty response');
          }

          const result = await readSSEStream(
            streamResponse,
            (_delta, fullResponse) => {
              latestPartialContent = fullResponse;
              setMessages((prev) =>
                prev.map((message) =>
                  message.id === placeholderAssistantId
                    ? { ...message, content: fullResponse }
                    : message
                )
              );
            }
          );

          return {
            ...result,
            placeholderAssistantId,
          };
        };

        const result = await retryWithExponentialBackoff(
          operation,
          userMessage.id,
          controller.signal
        );

        if (result.success) {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === userMessage.id
                ? { ...message, status: 'sent' as const }
                : message
            )
          );

          const assistantMeta = { ...options?.assistantMetadata };
          if (options?.toolContext) {
            assistantMeta.toolContext = options.toolContext;
          }

          const persistedMessage = await insertMessage({
            conversationId: conversationIdToUse,
            content: result.data.response,
            role: 'assistant',
            metadata: assistantMeta,
          });

          if (!persistedMessage) {
            setMessages((prev) =>
              prev.map((message) => {
                if (message.id === userMessage.id) {
                  return { ...message, status: 'failed' as const };
                }
                if (message.id === placeholderAssistantId) {
                  return { ...message, status: 'failed' as const };
                }
                return message;
              })
            );

            toast({
              title: 'Failed to save message',
              description:
                'The response was received but could not be saved. Please try again.',
              variant: 'destructive',
              action: (
                <button
                  type="button"
                  onClick={() => retryFailedMessage(userMessage.id)}
                  className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-input"
                >
                  Retry
                </button>
              ),
            });

            return false;
          }

          setMessages((prev) =>
            prev.map((message) =>
              message.id === placeholderAssistantId ? persistedMessage : message
            )
          );

          if (result.data?.title) {
            await updateConversationTitle(
              conversationIdToUse,
              result.data.title
            );
          }
        } else {
          setMessages((prev) =>
            prev.filter((message) => message.id !== placeholderAssistantId)
          );
        }

        return result.success;
      } catch (error) {
        if (isAbortError(error)) {
          const interruptedMetadata: Record<string, unknown> = {
            ...(options?.assistantMetadata ?? {}),
            ...(options?.toolContext
              ? { toolContext: options.toolContext }
              : {}),
            streaming: false,
            stopped: true,
          };

          const persistedMessage = await persistInterruptedAssistantMessage(
            conversationIdToUse,
            latestPartialContent,
            interruptedMetadata
          );

          setMessages((prev) =>
            prev.map((message) => {
              if (userMessageId && message.id === userMessageId) {
                return { ...message, status: 'sent' as const };
              }
              if (message.id !== placeholderAssistantId) {
                return message;
              }
              if (persistedMessage) {
                return persistedMessage;
              }
              return {
                ...message,
                status: 'interrupted' as const,
                metadata: {
                  ...message.metadata,
                  streaming: false,
                  stopped: true,
                },
              };
            })
          );
          return true;
        }

        console.error('Unexpected error in sendMessage:', error);

        if (placeholderAssistantId) {
          setMessages((prev) =>
            prev.filter((message) => message.id !== placeholderAssistantId)
          );
        }
        return false;
      } finally {
        endResponding();
      }
    },
    [
      currentConversationId,
      user,
      addMessage,
      insertMessage,
      messages,
      updateConversationTitle,
      supabase,
      trackEvent,
      persistInterruptedAssistantMessage,
      beginResponding,
      endResponding,
    ]
  );

  const stopResponding = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const deleteConversation = useCallback(
    async (conversationId: string) => {
      try {
        const { error } = await supabase
          .from('chat_conversations')
          .delete()
          .eq('id', conversationId);

        if (error) {
          throw error;
        }

        setConversations((prev) =>
          prev.filter((conversation) => conversation.id !== conversationId)
        );

        if (currentConversationId === conversationId) {
          setCurrentConversationId(null);
          setMessages([]);
        }
      } catch {
        // Silent error handling
      }
    },
    [supabase, currentConversationId]
  );

  return {
    conversations,
    currentConversationId,
    messages,
    isLoading,
    isResponding,
    pendingAssistantForMessageId,
    setMessages,
    setConversations,
    loadConversations,
    loadMessages,
    createConversation,
    switchConversation,
    sendMessage,
    addMessage,
    updateConversationTitle,
    deleteConversation,
    retryFailedMessage,
    stopResponding,
  };
};
